import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Paginator } from "primereact/paginator";
import { TabPanel, TabView } from "primereact/tabview";
import { useCallback, useState } from "react";
import useAsyncEffect from "../../hooks/useAsyncEffect";
import useSearchForm from "../../hooks/use-search-form/useSearchForm";
import RecordActionModal from "./components/record-action-modal/RecordActionModal";
import DeleteRecordModal from "./components/delete-record-modal/DeleteRecordModal";
import agent from "../../agent";
import styles from "./style.module.scss";

const initialPageConfig = {
    page: 0,
    pageSize: 10,
    totalElements: 0
};

const Catalog = ({ type = "monitoring" }) => {
    const [activeTab, setActiveTab] = useState(type === "monitoring" ? 1 : 0);
    const [tableData, setTableData] = useState([]);
    const [pageConfig, setPageConfig] = useState(initialPageConfig);
    const [addModalVisible, setAddModalVisible] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [currentModificationRecord, setCurrentModificationRecord] = useState(null);
    const [searchParams, setSearchParams] = useState(null);
    let tabs = [];

    switch (type) {
        case "monitoring":
            tabs = [
                {
                    code: "problem-situation",
                    label: "Проблемные ситуации",
                    modalLabel: "проблемную ситуацию"
                },
                {
                    code: "comment",
                    label: "Комментарий клиенту",
                    modalLabel: "комментарий клиенту"
                },
            ];
            break;
        case "billing":
            tabs = [
                {
                    code: "basic-values",
                    label: "Базовые величины",
                    modalLabel: "запись"
                },
                {
                    code: "indicators",
                    label: "Показатели для расчета",
                    modalLabel: "запись"
                },
                {
                    code: "taxes",
                    label: "Налоги и обязательные платежи в бюджет",
                    modalLabel: "запись"
                },
                {
                    code: "operation-types",
                    label: "Типы операций",
                    modalLabel: 'запись в справочник "Типы операций"'
                }
            ]
            break;
        default:
            tabs = [];
    }

    const formatDate = (date, isoDate = false) => {
        const dateObject = new Date(date);

        if (isoDate) {
            const d = new Date(date);
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, "0");
            const day = String(d.getDate()).padStart(2, "0");

            return `${year}-${month}-${day}`;
        }

        return ("0" + dateObject.getDate()).slice(-2) + "." + ("0" + (dateObject.getMonth() + 1)).slice(-2) + "."
            + dateObject.getFullYear() + " " + ("0" + dateObject.getHours()).slice(-2) + ":"
            + ("0" + dateObject.getMinutes()).slice(-2) + ":" + ("0" + dateObject.getSeconds()).slice(-2);
    }

    const fetchTableData = useCallback(async () => {
        const { code } = tabs[activeTab];
        const { page, pageSize } = pageConfig;

        let data;

        if (type === "monitoring") {
            if (searchParams && searchParams.name) {
                data = await agent.CatalogType.searchCatalogTypes(
                    code,
                    searchParams.name,
                    page,
                    pageSize
                );
            } else {
                data = await agent.CatalogType.listCatalogTypes(code, page, pageSize);
            }
        } else if (type === "billing") {
            if (!searchParams || code === "") {
                data = await agent.AccountingDictionary.listRecords(code, page, pageSize);
            } else {
                const params = searchParams.params || {};

                if (code === "") {
                    data = await agent.AccountingDictionary.listRecords(code, page, pageSize);
                } else {
                    data = await agent.AccountingDictionary.searchRecords(code, params, page, pageSize);
                }
            }
        }

        const formatedData = (data?.content ?? []).map((item) => ({
            ...item,
            createdDate: type === "monitoring" ? formatDate(item.createdDate) : undefined,
        }));

        setTableData(formatedData);

        const total = data?.totalElements ?? 0;
        setPageConfig((prev) =>
            prev.totalElements === total ? prev : { ...prev, totalElements: total }
        );
    }, [activeTab, pageConfig, type, searchParams]);

    useAsyncEffect(
        fetchTableData,
        [activeTab, pageConfig.page, pageConfig.pageSize, searchParams]
    );

    const handlePageChange = useCallback((e) => {
        setPageConfig({
            ...pageConfig,
            page: e.page,
            pageSize: e.rows,
        });
    }, [pageConfig]);

    const toggleAddModal = useCallback(() => {
        setAddModalVisible(true);
    }, []);

    const {
        searchForm,
        handleSubmit,
        reset
    } = useSearchForm(type, tabs[activeTab].code);

    const handleTabChange = (e) => {
        setActiveTab(e.index);
        setPageConfig((prev) => ({ ...prev, page: 0 }));
        setSearchParams(null);
        reset();
    };

    const buildBillingSearchParams = (code, filters) => {
        const params = {};

        const name = (filters.name || "").trim();
        const shortName = (filters.shortName || "").trim();
        const cipher = (filters.cipher || "").trim();
        const basicValueId = filters.basicValue || null;
        const amountIndicat = filters.amountIndicat;
        const startedRange = filters.startedAt || [];
        const endedRange = filters.endedAt || [];
        const activityRaw = filters.activity;

        const [startedFrom, startedTo] = startedRange;
        const [endedFrom, endedTo] = endedRange;

        if (name) {
            params.nameRu = name;
        }

        if (code === "basic-values") {
            if (shortName) {
                params.sign = shortName;
            }

            return params;
        }

        if (shortName) {
            params.shortNameRu = shortName;
        }

        if (basicValueId) {
            params.basicValueId = basicValueId;
        }

        if (amountIndicat !== "" && amountIndicat != null) {
            params.amountIndicat = amountIndicat;
        }

        if (cipher !== "") {
            params.cipher = cipher;
        }

        if (startedFrom) {
            params.startedAtFrom = formatDate(startedFrom, true);
        }

        if (startedTo) {
            params.startedAtTo = formatDate(startedTo, true);
        }

        if (endedFrom) {
            params.endedAtFrom = formatDate(endedFrom, true);
        }

        if (endedTo) {
            params.endedAtTo = formatDate(endedTo, true);
        }

        if ((code === "taxes" || code === "operation-types")
            && !(activityRaw === "" || activityRaw == null)) {
            params.activity = activityRaw === "Активен" || activityRaw === true;
        }

        if (code === "taxes") {
            if (filters.minIncome !== "" && filters.minIncome != null) {
                params.minIncome = filters.minIncome;
            }

            if (filters.maxIncome !== "" && filters.maxIncome != null) {
                params.maxIncome = filters.maxIncome;
            }
        }

        return params;
    };

    const searchByFilters = handleSubmit((filters) => {
        const { code } = tabs[activeTab];

        const nonEmptyEntries = Object.entries(filters)
            .filter(([, value]) => value !== "" && value != null);

        if (!nonEmptyEntries.length) {
            setSearchParams(null);
            setPageConfig((prev) => ({ ...prev, page: 0 }));

            return;
        }

        if (type === "monitoring") {
            const nameFilter = (filters.name || "").trim();

            if (!nameFilter) {
                setSearchParams(null);
            } else {
                setSearchParams({ code, name: nameFilter });
            }

            setPageConfig((prev) => ({ ...prev, page: 0 }));
            return;
        }

        const params = buildBillingSearchParams(code, filters);

        if (!Object.keys(params).length) {
            setSearchParams(null);
        } else {
            setSearchParams({ code, params });
        }

        setPageConfig((prev) => ({ ...prev, page: 0 }));
    });

    const resetSearch = useCallback(async () => {
        reset();
        setSearchParams(null);
        setPageConfig((prev) => ({ ...prev, page: 0 }));
    }, []);

    const tableActivityBody = ({ activity }) => (
        <p className={styles.activeChip}>{activity ? "Активен" : ""}</p>
    );

    const tableStartDateBody = ({ startedAt }) => {
        if (!startedAt) {
            return "";
        }

        const [ year, month, day ] = startedAt.split("-");

        return `${day}.${month}.${year}`;
    }

    const tableEndDateBody = ({ endedAt }) => {
        if (!endedAt) {
            return "";
        }

        const [ year, month, day ] = endedAt.split("-");

        return `${day}.${month}.${year}`;
    }

    const updateApprovedAt = async (record, approved) => {
        const code = tabs[activeTab].code;

        if (code !== "operation-types") {
            return;
        }

        const upsertDto = {
            nameRu: record.nameRu,
            cipherCode: record.cipherCode,
            startedAt: record.startedAt,
            endedAt: record.endedAt,
            approvedAt: approved ? formatDate(new Date(), true) : null,
            activity: record.activity,
            operationTypeTaxes: record.operationTypeTaxes ?? [],
        };

        await agent.AccountingDictionary.updateRecord(code, record.id, upsertDto);
        await fetchTableData();
    };

    const tableActionsBody = (record) => {
        const toggleEditModal = (record) => {
            setEditModalVisible(true);
            setCurrentModificationRecord(record);
        }

        const toggleDeleteModal = () => {
            setDeleteModalVisible(true);
            setCurrentModificationRecord(record);
        }

        const buttons = [
            ...(tabs[activeTab].code === "operation-types" ? [
                {
                    icon: "pi-check",
                    tooltip: "Утвердить запись",
                    color: "green",
                    disabled: Boolean(record.approvedAt),
                    onClick: () => updateApprovedAt(record, true),
                },
                {
                    icon: "pi-times",
                    tooltip: "Снять утверждение",
                    color: "red",
                    disabled: Boolean(!record.approvedAt),
                    onClick: () => updateApprovedAt(record, false),
                }
            ] : []),
            {
                icon: "pi-pen-to-square",
                tooltip: `Редактировать ${tabs[activeTab].modalLabel}`,
                color: null,
                onClick: () => toggleEditModal(record)
            },
            {
                icon: "pi-trash",
                tooltip: `Удалить ${tabs[activeTab].modalLabel}`,
                color: "red",
                onClick: () => toggleDeleteModal(record)
            }
        ]

        return (
            <div className={`p-d-flex ${styles.actionButtonsContainer}`}>
                {buttons.map(({icon, tooltip, onClick, color, disabled = false }, id) => (
                    <Button key={id}
                            className="p-text-bold"
                            onClick={onClick}
                            icon={`pi ${icon}`}
                            tooltip={tooltip}
                            disabled={disabled}
                            text
                            style={color ? { color } : null}
                    />
                ))}
            </div>
        )
    }

    const tabHeader = (tab) => <h2 className={`p-m-0 ${styles.tabHeader}`}>{tab.label}</h2>

    return (
        <>
            <RecordActionModal type={type}
                               method="GET"
                               currentTab={tabs[activeTab]}
                               isVisible={addModalVisible}
                               setIsVisible={setAddModalVisible}
                               fetchData={fetchTableData}
            />
            <RecordActionModal type={type}
                               method="PUT"
                               currentTab={tabs[activeTab]}
                               isVisible={editModalVisible}
                               setIsVisible={setEditModalVisible}
                               currentRecord={currentModificationRecord}
                               fetchData={fetchTableData}
            />
            <DeleteRecordModal type={type}
                               isVisible={deleteModalVisible}
                               setIsVisible={setDeleteModalVisible}
                               record={currentModificationRecord}
                               setRecord={setCurrentModificationRecord}
                               code={tabs[activeTab].code}
                               fetchData={fetchTableData}
            />
            <Button className={styles.addRecordButton} onClick={toggleAddModal} label="Добавить" icon="pi pi-plus"/>
            <TabView activeIndex={activeTab} onTabChange={(e) => handleTabChange(e)}>
                {tabs.map((tab, id) => (
                    <TabPanel className={`p-d-flex p-flex-column ${styles.catalogTab}`}
                              key={id}
                              header={tabHeader(tab)}
                              pt={{ headerAction: { style: { padding: "14px", borderBottomWidth: "2px" } } }}
                    >
                        {searchForm(
                            <div className={styles.searchButtonsContainer}>
                                <Button className={styles.searchFormButton} type="submit" label="Найти" onClick={searchByFilters}/>
                                <Button className={styles.searchFormButton} type="button" label="Сбросить" onClick={resetSearch}/>
                            </div>
                        )}
                        <DataTable className={styles.dataTable} value={tableData}>
                            <Column field="id"
                                    header="ID"
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.idColumn}`}
                            >
                            </Column>
                            <Column field="nameRu"
                                    header="Наименование"
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                            >
                            </Column>
                            <Column field="createdDate"
                                    header="Дата создания"
                                    hidden={type !== "monitoring"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                            >
                            </Column>
                            <Column field="createdBy"
                                    header="Автор"
                                    hidden={type !== "monitoring"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                            >
                            </Column>
                            <Column field={tabs[activeTab].code === "basic-values" ? "sign" : "shortNameRu"}
                                    header="Краткое обозначение"
                                    hidden={type !== "billing" || tab.code === "operation-types"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                            >
                            </Column>
                            <Column field="basicValue.sign"
                                    header="Базовая величина"
                                    hidden={type !== "billing" || tab.code === "basic-values" || tab.code === "operation-types"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                            >
                            </Column>
                            <Column field="amountIndicat"
                                    header="Размер"
                                    hidden={type !== "billing" || tab.code === "basic-values" || tab.code === "operation-types"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                            >
                            </Column>
                            <Column field="startedAt"
                                    header="Дата начала действия"
                                    hidden={type !== "billing" || tab.code === "basic-values"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                                    body={tableStartDateBody}
                            >
                            </Column>
                            <Column field="endedAt"
                                    header="Дата окончания действия"
                                    hidden={type !== "billing" || tab.code === "basic-values"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                                    body={tableEndDateBody}
                            >
                            </Column>
                            <Column field="activity"
                                    header="Состояние"
                                    hidden={type !== "billing" || tab.code === "basic-values"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                                    body={tableActivityBody}
                            >
                            </Column>
                            <Column field="cipherCode"
                                    header="Шифр"
                                    hidden={tab.code !== "operation-types"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}
                            >
                            </Column>
                            <Column field="maxIncome"
                                    header="Максимальный доход, кол-во МЗП"
                                    hidden={type !== "billing" || tab.code !== "taxes"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}>
                            </Column>
                            <Column field="minIncome"
                                    header="Минимальный доход, кол-во МЗП"
                                    hidden={type !== "billing" || tab.code !== "taxes"}
                                    headerClassName={styles.tableHeader}
                                    bodyClassName={`${styles.tableColumn} ${styles.mainDataColumn}`}>>
                            </Column>
                            <Column header="Действия" headerClassName={styles.tableHeader} body={tableActionsBody}>
                            </Column>
                        </DataTable>
                        <Paginator
                            currentPageReportTemplate="Показано с {first} по {last} из {totalRecords} записей"
                            template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                            first={pageConfig.page * pageConfig.pageSize}
                            rows={pageConfig.pageSize}
                            totalRecords={pageConfig.totalElements}
                            rowsPerPageOptions={[5, 10, 25, 50]}
                            onPageChange={handlePageChange}
                        />
                    </TabPanel> 
                ))}
            </TabView>
        </>
    )
}

export default Catalog;